import React, {type FC, ReactNode, useCallback, useState} from 'react';
import {IProduct} from '../../../../types/galleryTypes';
import {IProductSelectionAvailabilityMap} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/ProductVariantCalculator';
import s from './ProductOptions.scss';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import classNames from 'classnames';
import {useGlobals, withGlobals} from '../../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../../gallery/galleryGlobalStrategy';
import {Experiments} from '../../../../constants';
import {RevealAnimation} from '../../RevealAnimation/RevealAnimation';
import {ProductColorOption} from './ProductColorOption/ProductColorOption';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {getAllVisibleOptions, getAllVisibleColorOptions, getAllVisibleTextualOptions} from './utils/getVisibleOptions';
import {ProductTextualOption} from './ProductTextualOptions/ProductTextualOption';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import {useOptionFocusManagement} from './utils/hooks/useOptionFocusManagement';

type IProductOption = IProduct['options'][0];
export interface ProductOptionsChangeData {
  optionType: ProductOptionType;
}

export interface IProductOptionsProps extends IGalleryGlobalProps {
  product: IProduct;
  selectionIds: number[];
  isLoading?: boolean;
  readOnly?: boolean;

  onSelectionIdsChange(
    optionSelections: IProductOptionsProps['selectionIds'],
    changeData: ProductOptionsChangeData
  ): Promise<void>;

  variantsAvailability: IProductSelectionAvailabilityMap;
  isItemHovered: boolean;
}

export interface OptionRenderParams {
  option: IProduct['options'][number];
  availability: IProductSelectionAvailabilityMap[string];
  url?: string;
}

export enum DataHook {
  ProductOption = 'product-option',
  DropdownOption = 'product-options-dropdown',
  ColorOption = 'product-options-color',
  ColorOptionMore = 'product-options-color-more',
  OptionWrapper = 'product-option-wrapper',
  RevealAnimationWrapper = 'product-option-reveal-animation-wrapper',
  ButtonOption = 'product-option-button',
}

const ProductOptionsComponent: FC<IProductOptionsProps> = ({
  product,
  selectionIds,
  isLoading,
  onSelectionIdsChange,
  variantsAvailability,
  isItemHovered,
  readOnly,
}) => {
  const {productsManifest, isOptionsRevealEnabled} = useGlobals();
  const {experiments} = useExperiments();
  const isQualityOptionsStylingChangesEnabled = experiments.enabled(Experiments.EnableQualityOptionsStylingChanges);
  const [latestActiveOptionId, setLatestActiveOptionId] = useState<string>('');

  const isOptionSelected = useCallback(
    (option: IProductOption) => option.selections.find(({id}) => selectionIds.includes(id)) !== undefined,
    [selectionIds]
  );

  const isFirst = useCallback(
    (option: IProductOption) => {
      const options = getAllVisibleOptions(product.options);
      return options.length && options[0].id === option.id;
    },
    [product.options]
  );

  const isVisible = useCallback(
    (option: IProductOption) => {
      const options = getAllVisibleOptions(product.options);
      const index = options.findIndex(({id}) => option.id === id);
      return (
        isOptionSelected(option) ||
        (isFirst(option) && isItemHovered) ||
        (options[index] && isOptionSelected(options[index]))
      );
    },
    [isOptionSelected, isFirst, isItemHovered, product.options]
  );

  const {registerOptionRef} = useOptionFocusManagement({product, isOptionSelected});

  const shouldRenderLoader = useCallback(
    (optionId: string) => isLoading && latestActiveOptionId !== optionId,
    [isLoading, latestActiveOptionId]
  );

  const getColorOptions = useCallback(() => {
    const colorOptions = getAllVisibleColorOptions(product.options);

    const onColorOptionChanged = (
      selectedOptionIds: number[],
      changeData: ProductOptionsChangeData,
      optionId: string
    ) => {
      setLatestActiveOptionId(optionId);
      void onSelectionIdsChange(selectedOptionIds, changeData);
    };

    return colorOptions.map((option) => {
      return (
        <div
          key={option.id}
          ref={(ref) => registerOptionRef(option.id, ref)}
          className={classNames({
            [s.color]: isQualityOptionsStylingChangesEnabled,
            [s.oldColor]: !isQualityOptionsStylingChangesEnabled,
          })}
          data-hook={DataHook.OptionWrapper}>
          <ProductColorOption
            url={productsManifest[product.id].url}
            option={option}
            availability={variantsAvailability[option.id]}
            selectionIds={selectionIds}
            onSelectionIdsChange={(selectedOptionIds: number[], changeData: ProductOptionsChangeData) =>
              onColorOptionChanged(selectedOptionIds, changeData, option.id)
            }
            isLoading={shouldRenderLoader(option.id)}
          />
        </div>
      );
    });
  }, [
    isQualityOptionsStylingChangesEnabled,
    onSelectionIdsChange,
    productsManifest,
    selectionIds,
    shouldRenderLoader,
    variantsAvailability,
    product,
    registerOptionRef,
  ]);

  const wrapAnimation = useCallback(
    (child: ReactNode, isOptionVisible: boolean, preserveSpace: boolean) => {
      return isOptionsRevealEnabled ? (
        <RevealAnimation
          isVisible={isOptionVisible}
          preserveSpace={preserveSpace}
          data-hook={DataHook.RevealAnimationWrapper}>
          {child}
        </RevealAnimation>
      ) : (
        child
      );
    },
    [isOptionsRevealEnabled]
  );

  const getTextualOptions = useCallback(() => {
    const textualOptions = getAllVisibleTextualOptions(product.options);

    const handleTextualOptionChange = async (optionSelections: number[], changeData: ProductOptionsChangeData) => {
      const changedOptionId = textualOptions.find((option) =>
        option.selections.some((selection) => optionSelections.includes(selection.id))
      )?.id;

      /* istanbul ignore else */
      if (changedOptionId) {
        setLatestActiveOptionId(changedOptionId);
      }

      return onSelectionIdsChange(optionSelections, changeData);
    };

    return textualOptions.map((option) =>
      wrapAnimation(
        <div
          key={`${option.id}${isOptionsRevealEnabled ? isVisible(option) : ''}`}
          ref={(ref) => registerOptionRef(option.id, ref)}
          className={classNames({
            [s.dropdown]: isQualityOptionsStylingChangesEnabled,
            [s.oldDropdown]: !isQualityOptionsStylingChangesEnabled,
          })}
          data-hook={DataHook.OptionWrapper}>
          <ProductTextualOption
            option={option}
            availability={variantsAvailability[option.id]}
            selectionIds={selectionIds}
            readOnly={readOnly}
            isLoading={shouldRenderLoader(option.id)}
            onSelectionIdsChange={handleTextualOptionChange}
          />
        </div>,
        isVisible(option),
        isFirst(option)
      )
    );
  }, [
    isOptionsRevealEnabled,
    readOnly,
    wrapAnimation,
    isQualityOptionsStylingChangesEnabled,
    onSelectionIdsChange,
    selectionIds,
    shouldRenderLoader,
    variantsAvailability,
    product,
    registerOptionRef,
    isVisible,
    isFirst,
  ]);

  return (
    <>
      {getColorOptions()}
      <ConditionalRender by="showTextualOptions">{getTextualOptions()}</ConditionalRender>
    </>
  );
};

export const ProductOptions = withGlobals(ProductOptionsComponent);
